import { useState, useEffect, useRef, useCallback } from 'react';
import { useAppSelector } from '../../hooks/useAppSelector';
import { authStatusEnum } from '../../store/auth/authSlice';
import { proctoringImagePost } from '../../services/api/auth';
import AdaptiveCaptureProvider from '../../pages/AdaptiveAssessmentPage/components/AdaptiveImageCaptureProvider/AdaptiveCaptureProvider';
import CameraComponent from '../../pages/AttemptAssessmentPage/components/CameraComponent';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { setPhotoUploadPause } from '../../store/assessment/assessmentSlice';
import { questionType } from '../../store/assessment/questionSlice';

interface componentProps {
  accessKey: string | undefined;
}

const ImageCapture = ({ accessKey }: componentProps) => {
  const dispatch = useAppDispatch();
  const { current_question_number, questions_map, photo_upload_pause } =
    useAppSelector((state) => state.assessment);

  const { question_type } = useAppSelector((state) => state.question);

  const questionNoRef = useRef(1);
  const questionTypeRef = useRef<any>();
  const authState = useAppSelector((state) => state.auth);
  const photo = useRef<any>(null);
  const previousAnswer = useRef<any>(null);
  const answerChanged = useRef<any>(false);

  const [loading, setLoading] = useState(true);

  const localQuestionMap = useRef<any>(questions_map);

  const showAttempt = authState.status === authStatusEnum.AUTHENITICATED;
  const showFinish = authState.status === authStatusEnum.ENDED;

  const webcamRef = useRef<any>(null);

  const handlePhotoChange = (val: string) => {
    photo.current = val;
  };

  const capture = useCallback(() => {
    try {
      const imageSrc = webcamRef.current.getScreenshot();
      return imageSrc;
    } catch (err) {
      console.error(err);
    }
  }, [webcamRef, loading]);

  // console.log('current state', answerChanged.current);
  // console.log("Current Question Type", questionTypeRef.current)

  const postPhoto = async (
    photo: string,
    accessKey: string | undefined,
    questionNo: number
  ) => {
    if (
      !answerChanged.current &&
      questionTypeRef.current !== questionType.CODING
    ) {
      dispatch(setPhotoUploadPause(false));
      return;
    }
    try {
      await proctoringImagePost(accessKey, photo, questionNo);
    } catch (err) {
      // Do not throw any error here
    } finally {
      dispatch(setPhotoUploadPause(false));
    }
  };
  useEffect(() => {
    localQuestionMap.current = questions_map;
  }, [questions_map]);

  useEffect(() => {
    // Submission has completed and its token may already be cleared. Do not
    // start a second image upload from the component unmount/state change.
    if (showFinish) {
      photo.current = null;
      if (photo_upload_pause) dispatch(setPhotoUploadPause(false));
      return;
    }

    if (photo && photo.current) {
      const questionNo = Math.max(1, questionNoRef.current);
      const curQuestion = localQuestionMap.current[questionNo - 1];
      if (
        curQuestion.answered ||
        (questionTypeRef.current &&
          questionTypeRef.current === questionType.CODING)
      ) {
        postPhoto(photo.current, accessKey, questionNo);
      } else {
        dispatch(setPhotoUploadPause(false));
      }
      photo.current = null;
      questionNoRef.current = current_question_number;
    } else {
      // There was no photo
      dispatch(setPhotoUploadPause(false));
    }
  }, [current_question_number, showFinish, accessKey, photo_upload_pause]);

  useEffect(() => {
    // console.log('I ran');
    if (previousAnswer && previousAnswer.current) {
      // console.log('inside here');
      if (
        previousAnswer.current.number === current_question_number &&
        previousAnswer.current?.submitted !==
          questions_map[current_question_number - 1]?.submitted_option
      ) {
        answerChanged.current = true;
      } else if (previousAnswer.current.number !== current_question_number) {
        answerChanged.current = false;
      }
    } else {
      answerChanged.current = true;
    }
    previousAnswer.current = {
      submitted: questions_map[current_question_number - 1]?.submitted_option,
      number: current_question_number,
    };
  }, [current_question_number, questions_map]);

  useEffect(() => {
    questionTypeRef.current = question_type;
  }, [question_type]);

  return (
    <>
      {showAttempt && !showFinish && !photo_upload_pause ? (
        <AdaptiveCaptureProvider
          key={current_question_number}
          setPhoto={handlePhotoChange}
          getPhoto={capture}
        />
      ) : (
        <></>
      )}
      {showAttempt && !showFinish ? (
        <CameraComponent
          cameraRef={webcamRef}
          onLoad={() => setLoading(false)}
        />
      ) : (
        <></>
      )}
    </>
  );
};

export default ImageCapture;
